import { createContext, useContext } from "react";
import { Info } from "lucide-react";
import { Label } from "@/components/shadcn/label";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/shadcn/tooltip";
import { PinHandle } from "@/components/SpotflowV2/Sidebar/PinHandle";
import { ReleaseStatusBadge } from "@/components/UI/ReleaseStatusBadge";

export const PinFieldContext = createContext<{
  nodeId?: string;
  fieldKey?: string;
}>({});

// Whether the field being rendered sits behind a feature flag, i.e. is not
// generally available yet. Supplied by the per-field wrapper in
// ConfigFormRenderer so the badge lands inline with the label, rather than
// every field component threading a prop through BaseConfig's 114-case
// switch. Same trick PinFieldContext already uses.
export const BetaFieldContext = createContext(false);

export const NoxusFormLabel = ({
  label,
  tooltip,
  tooltipHref,
  isRequired,
  isDisabled,
  showOptionalLabel,
  name,
  noMargin,
  slot,
}: {
  label: string | React.ReactNode;
  tooltip?: string;
  tooltipHref?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  showOptionalLabel?: boolean;
  name?: string;
  noMargin?: boolean;
  slot?: React.ReactNode;
}) => {
  const { nodeId, fieldKey } = useContext(PinFieldContext);
  const isBeta = useContext(BetaFieldContext);

  return (
    <Label
      htmlFor={name}
      className={`relative flex justify-between items-center gap-2 ${
        noMargin ? "" : "mb-2"
      }`}
    >
      <div className="flex justify-start items-center gap-1">
        <span
          className={`text-sm font-medium text-foreground leading-none ${
            isDisabled ? "text-muted-foreground" : "text-foreground"
          }`}
        >
          {label}
          {isRequired && (
            <span className="text-destructive ml-0.5 leading-none">*</span>
          )}
        </span>

        {tooltip && (
          <Tooltip>
            <TooltipTrigger asChild>
              {tooltipHref ? (
                <a
                  href={tooltipHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex cursor-pointer text-muted-foreground hover:text-foreground"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Info size={14} strokeWidth={1.25} />
                </a>
              ) : (
                <Info
                  size={14}
                  strokeWidth={1.25}
                  className="text-muted-foreground"
                />
              )}
            </TooltipTrigger>
            <TooltipContent side="top" className="z-1400 max-w-xs">
              {tooltip}
            </TooltipContent>
          </Tooltip>
        )}
        {isBeta && <ReleaseStatusBadge status="beta" className="shrink-0" />}
        {slot}
        {nodeId && fieldKey && (
          <PinHandle nodeId={nodeId} fieldKey={fieldKey} />
        )}
      </div>
      {!isRequired && showOptionalLabel && (
        <span
          className={`text-xs font-normal leading-none ${
            isDisabled ? "text-muted-foreground" : "text-muted-foreground"
          }`}
        >
          Optional
        </span>
      )}
    </Label>
  );
};
